import './ConfirmDelete.scss';

import classNames from 'classnames';
import { getDirectParent } from '@lib/popups';
import { TooltipPlacement } from 'antd/es/tooltip';

import { ButtonProps, Popconfirm, Tooltip } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC, MESSAGES_COPY } from '@sonar-software/sonar-ts-constants';
import { haltClickEventPropagation, handleEscape, off, on } from '@lib/events';
import React, { PropsWithChildren, useMemo } from 'react';
import { useBoolean, useOnMount, useOnUnmount } from '@hooks';

export interface ConfirmDeleteProps {
	cancelButtonProps?: ButtonProps;
	className?: string;
	confirmButtonProps?: ButtonProps;
	getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
	id?: string;
	onConfirm: () => void;
	onCancel?: () => void;
	placement?: TooltipPlacement;
	title?: string;
	tooltip?: string;
}

export const ConfirmDelete: React.FC<PropsWithChildren<ConfirmDeleteProps>> = ({
	cancelButtonProps,
	children,
	className,
	confirmButtonProps,
	getPopupContainer,
	id,
	placement = 'bottom',
	onConfirm,
	onCancel,
	title = MESSAGES_COPY.ARE_YOU_SURE,
	tooltip
}) => {
	const [ isOpen, setIsOpen ] = useBoolean(false);

	const handleEscapePressed = useMemo(() => handleEscape(setIsOpen.off), [ setIsOpen.off ]);

	useOnMount(() => {
		on('keyup', handleEscapePressed);
	});

	useOnUnmount(() => {
		off('keyup', handleEscapePressed);
	});

	const getContainer = getPopupContainer ?
		(triggerNode: HTMLElement) => getPopupContainer(getDirectParent(triggerNode) as HTMLElement) :
		getDirectParent;

	return (
		<div
			className={ classNames('confirm-delete-wrapper', className) }
			onClick={ haltClickEventPropagation }>
			<Tooltip
				placement={ placement }
				title={ tooltip }
				trigger={ [ 'hover', 'click' ] }>
				<Popconfirm
					cancelButtonProps={ cancelButtonProps }
					cancelText={ cancelButtonProps?.value || COPY_GENERIC.CANCEL }
					className='confirm-delete'
					classNames={ { root: 'confirm-delete-popup' } }
					getPopupContainer={ getContainer }
					id={ id }
					okButtonProps={ confirmButtonProps }
					okText={ confirmButtonProps?.value || COPY_SPECIFIC.DELETE }
					okType='danger'
					onCancel={ onCancel }
					onConfirm={ onConfirm }
					onOpenChange={ setIsOpen.toggle }
					open={ isOpen }
					placement={ placement }
					title={ title }>
					{ children }
				</Popconfirm>
			</Tooltip>
		</div>
	);
};
